import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Users, LifeBuoy, AlertTriangle, Database, FileText, LogOut, FolderTree, Wrench } from 'lucide-react';
import { useState, useEffect } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';

const ADMIN_SECTIONS = [
  { id: 'users', label: 'Subscriptions', path: '/admin/users', icon: Users },
  { id: 'tickets', label: 'Help Tickets', path: '/admin/tickets', icon: LifeBuoy },
  { id: 'errors', label: 'Error Logs', path: '/admin/errors', icon: AlertTriangle },
  { id: 'plans', label: 'Plans', path: '/admin/plans', icon: Database },
  { id: 'payments', label: 'Payments', path: '/admin/payments', icon: FileText },
  { id: 'forms', label: 'Forms Library', path: '/admin/forms', icon: FolderTree },
  { id: 'pdf-tools', label: 'PDF Tools', path: '/admin/pdf-tools', icon: Wrench },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (!userData.is_super_admin) {
          toast.error('Access denied: Super admin only');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error:', error);
      navigate('/auth');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Super Admin</h1>
                <p className="text-xs text-slate-500">Control Panel</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="rounded-xl"
              >
                Exit Admin Mode
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex gap-2 mt-6 border-b border-slate-200">
            {ADMIN_SECTIONS.map(section => {
              const Icon = section.icon;
              const isActive = location.pathname === section.path;
              return (
                <button
                  key={section.id}
                  onClick={() => navigate(section.path)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
                    isActive
                      ? 'border-violet-600 text-violet-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Outlet />
      </div>
    </div>
  );
}
